import { useCallback, useState } from 'react';
import { toast } from 'react-toastify';
import { useAuthModal } from '../providers/auth-modal.provider';
import { useSavedStatus } from '../providers/saved-status.provider';
import { useUserState } from '../providers/user-state.provider.ts';
import { removeSavedVideo, saveVideo as saveVideoRequest } from '../services/library.service';
import { Routes } from '../types/constants';

const setPendingSave = (videoId: string) => {
    localStorage.setItem('pendingSavedVideoId', videoId);
    localStorage.setItem('redirectAfterLogin', window.location.href);
};

const shouldShowToast = (): boolean => {
    if (typeof window === 'undefined') {
        return true;
    }

    if (window.location.pathname !== Routes.MY_LIBRARY) {
        return true;
    }

    const params = new URLSearchParams(window.location.search);
    const tab = params.get('tab') ?? 'saved';
    return tab !== 'saved';
};

/**
 * Save/unsave videos with login-aware behavior.
 */
export const useSaveVideo = () => {
    const { getUserInfo, isLoggedIn } = useUserState();
    const { openModal } = useAuthModal();
    const { isSaved, addSaved, removeSaved } = useSavedStatus();
    const [isSaving, setIsSaving] = useState(false);

    const saveVideo = useCallback(async (videoId: string, tokenOverride?: string) => {
        if (!videoId) {
            return false;
        }

        const userInfo = getUserInfo();
        const token = tokenOverride ?? userInfo?.token;
        if (!token) {
            setPendingSave(videoId);
            openModal('sign_in', window.location.href);
            return false;
        }

        setIsSaving(true);
        try {
            await saveVideoRequest(videoId, token);
            addSaved(videoId);
            if (shouldShowToast()) {
                toast.success('Saved to My Library.');
            }
            return true;
        } catch (error) {
            toast.error('Unable to save this video right now.');
            return false;
        } finally {
            setIsSaving(false);
        }
    }, [addSaved, getUserInfo, openModal]);

    const unsaveVideo = useCallback(async (videoId: string, tokenOverride?: string) => {
        if (!videoId) {
            return false;
        }

        const userInfo = getUserInfo();
        const token = tokenOverride ?? userInfo?.token;
        if (!token) {
            setPendingSave(videoId);
            openModal('sign_in', window.location.href);
            return false;
        }

        setIsSaving(true);
        try {
            await removeSavedVideo(videoId, token);
            removeSaved(videoId);
            if (shouldShowToast()) {
                toast.success('Removed from My Library.');
            }
            return true;
        } catch (error) {
            toast.error('Unable to remove this video right now.');
            return false;
        } finally {
            setIsSaving(false);
        }
    }, [getUserInfo, openModal, removeSaved]);

    const toggleSaved = useCallback(async (videoId: string) => {
        if (!videoId) {
            return false;
        }

        if (!isLoggedIn) {
            setPendingSave(videoId);
            openModal('sign_in', window.location.href);
            return false;
        }

        return isSaved(videoId)
            ? await unsaveVideo(videoId)
            : await saveVideo(videoId);
    }, [isLoggedIn, isSaved, openModal, saveVideo, unsaveVideo]);

    return {
        isSaving,
        isSaved,
        saveVideo,
        unsaveVideo,
        toggleSaved
    };
};
